import React, { useEffect, useState } from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import LoadingOverlay from '../components/common/LoadingOverlay';
import { Box, Paper, Typography, Button, Alert, Container } from '@mui/material';
import LoginIcon from '@mui/icons-material/Login';
import logo from '../assets/logo.svg';

const Login = () => {
  const { user, isLoading, login, logout } = useAuth();
  const [searchParams] = useSearchParams();
  const [error, setError] = useState(null);

  useEffect(() => {
    const errorParam = searchParams.get('error');
    if (errorParam === 'not_whitelisted') {
      setError('Access denied: Your email is not authorized to access the admin portal. Please contact an administrator for whitelist access.');
    } else if (errorParam === 'auth_error') {
      setError('Authentication error: Please try again or contact support if the problem persists.');
    }
  }, [searchParams]);

  if (isLoading) {
    return <LoadingOverlay message="Loading..." />;
  }

  if (user) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(45deg, #f3f4f6 30%, #ffffff 90%)',
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={3}
          sx={{
            p: 4,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            borderRadius: 2,
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(10px)',
            border: '1px solid rgba(255, 255, 255, 0.3)',
          }}
        >
          {/* Logo */}
          <Box
            component="img"
            src={logo}
            alt="QualiteeMed Logo"
            sx={{
              width: 180,
              height: 'auto',
              mb: 3,
            }}
          />

          {error && (
            <Alert 
              severity="error" 
              sx={{ 
                width: '100%', 
                mb: 3,
                '& .MuiAlert-message': {
                  width: '100%'
                }
              }}
            >
              {error}
            </Alert>
          )}

          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            sx={{
              fontWeight: 600,
              color: '#1a365d',
              textAlign: 'center',
              mb: 1,
            }}
          >
            Admin Portal
          </Typography>

          <Typography
            variant="body1"
            color="text.secondary"
            sx={{
              mb: 4,
              textAlign: 'center',
            }}
          >
            Please log in to access the admin portal
          </Typography>

          <Button
            onClick={login}
            variant="contained"
            size="large"
            startIcon={<LoginIcon />}
            sx={{
              py: 1.5,
              px: 4,
              borderRadius: 2,
              textTransform: 'none',
              fontSize: '1.1rem',
              background: 'linear-gradient(45deg, #e31837 30%, #ff1744 90%)',
              boxShadow: '0 3px 5px 2px rgba(227, 24, 55, .3)',
              '&:hover': {
                background: 'linear-gradient(45deg, #d31731 30%, #f01539 90%)',
                transform: 'translateY(-1px)',
                boxShadow: '0 4px 6px 2px rgba(227, 24, 55, .4)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            Log in
          </Button>
        </Paper>
      </Container>
    </Box>
  );
};

export default Login; 